import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { Bell, Search, Settings, User, TrendingUp, TrendingDown, AlertCircle, Filter } from 'lucide-react';

// Helper function to generate mock data for different time ranges
const generateMockData = (range) => {
  const now = new Date();
  const data = [];
  let intervalMinutes;
  let dataPoints;

  switch (range) {
    case '1H':
      intervalMinutes = 5;
      dataPoints = 12;
      break;
    case '1D':
      intervalMinutes = 60;
      dataPoints = 24;
      break;
    case '1W':
      intervalMinutes = 60 * 24;
      dataPoints = 7;
      break;
    case '1M':
      intervalMinutes = 60 * 24 * 2;
      dataPoints = 15;
      break;
    case '3M':
      intervalMinutes = 60 * 24 * 7;
      dataPoints = 12;
      break;
    case 'YTD':
      intervalMinutes = 60 * 24 * 30;
      dataPoints = Math.floor((now - new Date(now.getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24 * 30));
      break;
    default:
      intervalMinutes = 60;
      dataPoints = 24;
  }

  for (let i = dataPoints - 1; i >= 0; i--) {
    const date = new Date(now.getTime() - i * intervalMinutes * 60 * 1000);
    data.push({
      name: date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        hour: 'numeric', 
        minute: 'numeric' 
      }),
      sentiment: Math.floor(Math.random() * 30) + 60, // Random sentiment between 60 and 90
      price: Math.floor(Math.random() * 50) + 130, // Random price between 130 and 180
      volume: Math.floor(Math.random() * 1000) + 500 // Random volume between 500 and 1500
    });
  }

  return data;
};

const mockAssets = [
  { name: 'AAPL', sentiment: 78, change: '+2.5%', volume: 5000000 },
  { name: 'GOOGL', sentiment: 82, change: '+1.8%', volume: 2000000 },
  { name: 'MSFT', sentiment: 75, change: '-0.5%', volume: 3000000 },
  { name: 'AMZN', sentiment: 70, change: '+0.7%', volume: 2500000 },
  { name: 'TSLA', sentiment: 85, change: '+3.2%', volume: 4000000 },
];

const mockNewsSentiment = [
  { name: 'Positive', value: 60 },
  { name: 'Neutral', value: 30 },
  { name: 'Negative', value: 10 },
];

const mockSocialMediaMentions = [
  { platform: 'Twitter', mentions: 1200 },
  { platform: 'Reddit', mentions: 800 },
  { platform: 'StockTwits', mentions: 600 },
  { platform: 'LinkedIn', mentions: 400 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export default function SapianInteractiveDashboard() {
  const [selectedAsset, setSelectedAsset] = useState('AAPL');
  const [timeRange, setTimeRange] = useState('1D');
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    setChartData(generateMockData(timeRange));
  }, [timeRange]);

  return (
    <div className="bg-gray-100 min-h-screen">
      <nav className="bg-white shadow-md p-4">
        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold text-blue-600">Sapian</div>
          <div className="flex items-center space-x-6">
            <div className="relative">
              <input type="text" placeholder="Search assets..." className="pl-8 pr-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300" />
              <Search className="absolute left-2 top-2.5 text-gray-400" size={18} />
            </div>
            <Bell className="text-gray-600 cursor-pointer" />
            <Settings className="text-gray-600 cursor-pointer" />
            <User className="text-gray-600 cursor-pointer" />
          </div>
        </div>
      </nav>
      
      <div className="container mx-auto p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Market Sentiment Dashboard</h1>
            <p className="text-gray-600">Real-time AI-powered sentiment analysis for smarter trading decisions</p>
          </div>
          <div className="flex space-x-2">
            {['1H', '1D', '1W', '1M', '3M', 'YTD'].map((range) => (
              <button
                key={range}
                className={`px-3 py-1 rounded ${timeRange === range ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'}`}
                onClick={() => setTimeRange(range)}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Sentiment Analysis: {selectedAsset}</h2>
                <div className="flex items-center space-x-2">
                  <Filter className="text-gray-400" size={18} />
                  <select className="border rounded p-1">
                    <option>Sentiment + Price</option>
                    <option>Sentiment + Volume</option>
                    <option>Price + Volume</option>
                  </select>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="sentiment" stroke="#8884d8" name="Sentiment Score" />
                  <Line yAxisId="right" type="monotone" dataKey="price" stroke="#82ca9d" name="Price" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">News Sentiment</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={mockNewsSentiment}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {mockNewsSentiment.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Social Media Mentions</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={mockSocialMediaMentions}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="mentions" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Watchlist</h2>
              <div className="space-y-4">
                {mockAssets.map((asset) => (
                  <div key={asset.name} className="flex justify-between items-center p-2 hover:bg-gray-100 rounded cursor-pointer" onClick={() => setSelectedAsset(asset.name)}>
                    <div>
                      <div className="font-semibold">{asset.name}</div>
                      <div className="text-sm text-gray-600">Sentiment: {asset.sentiment}</div>
                    </div>
                    <div>
                      <div className={asset.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}>{asset.change}</div>
                      <div className="text-sm text-gray-600">Vol: {asset.volume.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">AI Insights</h2>
              <ul className="space-y-4">
                <li className="flex items-start space-x-2">
                  <TrendingUp className="text-green-500 flex-shrink-0" />
                  <span>Positive sentiment shift detected for tech sector</span>
                </li>
                <li className="flex items-start space-x-2">
                  <TrendingUp className="text-green-500 flex-shrink-0" />
                  <span>Increasing mentions of "AI" in financial news (↑15% from yesterday)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <TrendingDown className="text-red-500 flex-shrink-0" />
                  <span>Potential bearish trend forming for energy stocks</span>
                </li>
                <li className="flex items-start space-x-2">
                  <AlertCircle className="text-yellow-500 flex-shrink-0" />
                  <span>Unusual social media activity detected for $TSLA</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Recent News</h2>
              <div className="space-y-4">
                <div className="border-b pb-2">
                  <h3 className="font-semibold">Tech Giants Announce New AI Partnerships</h3>
                  <p className="text-sm text-gray-600">2 hours ago • Sentiment: Positive</p>
                </div>
                <div className="border-b pb-2">
                  <h3 className="font-semibold">Federal Reserve Hints at Potential Rate Cut</h3>
                  <p className="text-sm text-gray-600">4 hours ago • Sentiment: Neutral</p>
                </div>
                <div>
                  <h3 className="font-semibold">Oil Prices Surge Amid Middle East Tensions</h3>
                  <p className="text-sm text-gray-600">6 hours ago • Sentiment: Negative</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}